import Download from "@mui/icons-material/Download";
import Print from "@mui/icons-material/Print";
import { IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { AppTable } from "components/molecules";
import {
  ECommonDialogType,
  openDialog,
} from "components/molecules/CommonDialog";
import { RoleHelper, TableHelper } from "helpers";
import { IPrintLORequest } from "model";
import { IDeliveryProgress } from "model/LoadingOrder/IDeliveryProgress";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { LoadingOrderActions, PrintActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { theme } from "themes";
import { I18nTranslator, translations } from "utils/i18n";
import PrintDeliveryProgressModal from "./PrintDeliveryProgressModal";

interface IProductListProps {
  deliveryOrderHeaderId: string;
}
const DeliveryProgress = (props: IProductListProps) => {
  const { deliveryOrderHeaderId } = props;
  const dispatch = useDispatch();
  const data = useAppSelector(
    (state) => state.LoadingOrderReducer.deliveryProgress.data!
  );
  const headerAction = useAppSelector(
    (state) => state.LoadingOrderReducer.action
  );
  const action = useAppSelector(
    (state) => state.LoadingOrderReducer.deliveryProgress.action
  );
  const [searchText, setSearchText] = useState("");

  const [openPrintProgressDelivery, setOpenPrintProgressDelivery] =
    useState(false);
  const [currentDeliveryProgress, setCurrentDeliveryProgress] = useState<
    IDeliveryProgress | undefined
  >();

  const handleOpenPrint = (data: IDeliveryProgress) => {
    setOpenPrintProgressDelivery(true);
    setCurrentDeliveryProgress(data);
  };
  const onConfirmPrint = (printQty: number) => {
    setOpenPrintProgressDelivery(false);
    _printLO(printQty);
  };

  const _printLO = (printQty: number) => {
    const request: IPrintLORequest = {
      deliveryOrderSumId: currentDeliveryProgress?.id,
      batchCode: currentDeliveryProgress?.batchCode,
      companyCode: currentDeliveryProgress?.companyCode,
      companyName: currentDeliveryProgress?.companyName,
      productName: currentDeliveryProgress?.productName,
      deliveryDate: currentDeliveryProgress?.deliveryDate,
      printQty: printQty,
      manufacturingDate: currentDeliveryProgress?.manufacturingDate,
      expiryDate: currentDeliveryProgress?.expiryDate,
    };
    dispatch(PrintActions.printLO.request(request));
  };

  const _getAllLoadingOrder = (pageNumber = 1) => {
    dispatch(
      LoadingOrderActions.getDeliveryProgress.request({
        pageNumber: pageNumber,
        pageSize: 100,
        deliveryOrderHeaderId: deliveryOrderHeaderId,
        searchText: searchText,
      })
    );
  };
  const _handlePageSizeChange = (pageSize: number) => {
    dispatch(
      LoadingOrderActions.getDeliveryProgress.request({
        pageNumber: 1,
        pageSize: pageSize,
        deliveryOrderHeaderId: deliveryOrderHeaderId,
        searchText: searchText,
      })
    );
  };
  useEffect(() => {
    _getAllLoadingOrder();
  }, [
    searchText,
    headerAction.includes(
      LoadingOrderActions.importLoadingOrderDocument.successName
    ),
  ]);
  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "id",
      headerName: I18nTranslator(translations.receive.action),
      size: 120,
      align: "center",
      sortable: true,
      renderCell: ({ row }) => (
        <Stack direction={"row"}>
          <IconButton
            onClick={() => handleOpenPrint(row as IDeliveryProgress)}
            sx={{
              display: !RoleHelper.isSuperAdmin() ? "none" : "inline",
            }}
            disabled={!RoleHelper.isSuperAdmin()}
          >
            <Print sx={{ color: theme.colors.primary.main }} fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }),
    TableHelper.createCell({
      field: "deliveryDate",
      headerName: I18nTranslator(translations.receive.deliveryDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "companyCode",
      headerName: I18nTranslator(translations.receive.companyId),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "companyName",
      headerName: I18nTranslator(translations.receive.companyName),
      size: "full",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productId),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.receive.productName),
      size: "medium",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "hasQrCode",
      headerName: I18nTranslator(translations.receive.qrCode),
      size: "small",
      align: "left",
      renderCell: ({ value }) => (
        <Typography>{value ? "QR CODE" : ""}</Typography>
      ),
    }),

    TableHelper.createCell({
      field: "batchCode",
      headerName: I18nTranslator(translations.receive.batchCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "packSize",
      headerName: I18nTranslator(translations.receive.cartonSpecification),
      size: 200,
      align: "right",
    }),
    TableHelper.createCell({
      field: "doQuantity",
      headerName: I18nTranslator(translations.receive.quantityLO),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "suggestPickQuantity",
      headerName: I18nTranslator(translations.receive.suggestPickQuantity),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "preparedQty",
      headerName: I18nTranslator(translations.receive.prepared),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "deliveredQty",
      headerName: I18nTranslator(translations.receive.delivered),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "needPrepareQty",
      headerName: I18nTranslator(translations.receive.needPrepare),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "needDeliveryQty",
      headerName: I18nTranslator(translations.receive.needDeliver),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.receive.productCodeOld),
      size: "small",
      align: "left",
      sortable: true,
    }),
  ];
  return (
    <Fragment>
      <AppTable<IDeliveryProgress>
        loading={action.includes(
          LoadingOrderActions.getAllLoadingOrderProducts.requestName
        )}
        data={data}
        columns={COLUMNS}
        hideAddNew
        onSearch={setSearchText}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchDeliveryProgress
        )}
        onChangePage={(pageNumber: number) => _getAllLoadingOrder(pageNumber)}
        removeActionColumn
        hideHeader
        defaultHeaderTable={230}
        onPageSizeChange={_handlePageSizeChange}
      />
      <PrintDeliveryProgressModal
        open={openPrintProgressDelivery}
        onConfirmPrint={onConfirmPrint}
        onClose={() => setOpenPrintProgressDelivery(false)}
      />
    </Fragment>
  );
};

export default DeliveryProgress;
